/* ── 170 §1d [RULED 2026-09-08, Laurent] — OPENING THE ARCHIVED PDF OF AN EMAIL
 *
 * A SIBLING of `docItemLink.ts`, for the reason that file's own header gives:
 * `emailLink.ts` is a file about the `message:` URI SCHEME, and this has no
 * scheme. What it has is a matter-scoped lookup of a stored pointer, which is
 * the SHAPE `docItemLink.ts` already observes — "resolve an opaque provenance
 * id through a route, cache the answer for the session, open it, and name the
 * reason when it cannot be opened". That shape is three lines each and it is
 * observed here too rather than abstracted: the route differs, the cache key
 * differs, and what a failure MEANS differs.
 *
 * The id resolved here is the RFC Message-ID (`TimeEntry.emailGraphId`), not a
 * drive item id — the server holds the drive item and hands back a live
 * `webUrl`, repairing its own stored copy on the way past.
 */
import { api } from "../api";
import { openDocHref } from "../components/SharedUI";
import type { TFn } from "./taxonomyTypes";

/** The two matter kinds, spelled as `api.ts` spells them. */
export interface EmailPdfMatter {
  caseId?: number | string | null;
  ccId?: number | string | null;
}

/* Cached for the SESSION and keyed by MATTER AND MESSAGE: the same email
   archived on two matters is two files, and one answer must not stand in for
   the other. Nothing is persisted — the server repairs a stale `webUrl` on
   every resolve, so a cached one is only ever as old as this tab. */
const resolved = new Map<string, string>();

/* Built in two steps, not one nested template. `String()` on each id because
   the union includes `number`, and a bare interpolation of one is what
   `restrict-template-expressions` refuses — this file is absent from
   `eslint-baseline.json`, and the ratchet requires such a file to carry NO
   warnings at all rather than inherit its sibling's twenty. */
function keyOf(m: EmailPdfMatter, id: string): string {
  const matter = m.ccId != null ? `cc:${String(m.ccId)}` : `ca:${String(m.caseId)}`;
  return `${matter}|${id}`;
}

/* ONE non-throwing cause, so one sentence. Every failure the route can produce
   — 400, 404 `emailArchive.no_pointer`, 502 `emailArchive.pointer_unresolvable`
   — arrives through `api()`'s envelope already carrying the server's own
   sentence, exactly as `docItemLink.ts:60-68` measured for its own route. What
   is left is a 200 with no url, which cannot happen but is answered anyway. */

/* THE TOAST IS REACHED THROUGH `globalThis`, AND THAT IS NOT A STYLE CHOICE.
   `eslint.config.mjs:42` declares no browser globals for `src/**`, so a bare
   `window` is `no-undef` — the two siblings carry that warning as inherited
   debt, and a file the baseline does not know may carry none at all. But
   `globalThis` alone does not type-check either: `lxToast` is declared on
   `interface Window` (global.d.ts), and `typeof globalThis` has no index
   signature. So the property is named ONCE here, with its real type and no
   `any`, rather than widening a shared declaration file for one call site.
   The type is `Window["lxToast"]` rather than one written here: base
   `no-unused-vars` reports every NAMED PARAMETER of a function type and this
   config sets no ignore pattern, so declaring `(message: string, kind?: string)
   => void` costs two warnings for two names nothing reads. Reusing the declared
   type has none, and cannot drift from it either. */
type LxToastFn = NonNullable<Window["lxToast"]>;
const lxToast = (): LxToastFn | undefined =>
  // `NonNullable` rather than `Window["lxToast"]` directly: that type is already
  // optional, so `lxToast?: Window["lxToast"]` carries `| undefined` twice and
  // sonarjs calls the second marker redundant — while a REQUIRED property makes
  // the cast itself unsound, because `typeof globalThis` does not have it.
  (globalThis as { lxToast?: LxToastFn }).lxToast;

function say(t: TFn, message?: string): void {
  const fallback = t("emailPdfLink.unavailable", {
    fallback: "The archived PDF of this email cannot be opened from here.",
  });
  lxToast()?.(message?.trim() ? message : fallback, "warning");
}

/**
 * Open the archived PDF of the email a time entry was recorded from. Returns
 * true when a tab was opened.
 *
 * Never throws: it is called from an `onClick`, and a rejection there is the
 * swallowed-throw shape `no-silent-catch` exists for.
 *
 * `internetMessageId` is typed `string` rather than `unknown`: the caller has
 * the value and stringifies it, and taking `unknown` here only moved the
 * `no-base-to-string` question one line.
 */
export async function openArchivedEmailPdf(
  matter: EmailPdfMatter,
  internetMessageId: string,
  t: TFn,
): Promise<boolean> {
  const id = internetMessageId.trim();
  if (!id || (matter.caseId == null && matter.ccId == null)) {
    say(t);
    return false;
  }
  const hit = resolved.get(keyOf(matter, id));
  if (hit !== undefined) return openDocHref(hit);
  const base =
    matter.ccId != null
      ? `/collective-cases/${String(matter.ccId)}/emails`
      : `/cases/${String(matter.caseId)}/emails`;
  try {
    const d = await api<{ webUrl?: string | null } | null>(
      `${base}/archived-pdf/${encodeURIComponent(id)}`,
    );
    const url = d?.webUrl;
    /* An explicit emptiness test rather than `url || fallback`:
       `prefer-nullish-coalescing` refuses the `||`, and `??` is the WRONG
       operator here — it would accept an empty string as an answer and hand it
       to `openDocHref`. */
    if (typeof url !== "string" || !url) {
      say(t);
      return false;
    }
    resolved.set(keyOf(matter, id), url);
    return openDocHref(url);
  } catch (e) {
    const m = (e as { message?: string } | null)?.message;
    say(t, typeof m === "string" ? m : undefined);
    return false;
  }
}
